import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { buildApiClient } from './apiClient';

type Membership = { tenant: { id: number; code: string }; warehouse: { id: number; code: string } | null; role: string };

/** Per-warehouse entry as returned by the extended /me payload (A5). */
type PerWarehouseEntry = {
  id: number;
  code: string;
  name: string;
  roles: string[];
  is_default: boolean;
};

type MeData = {
  user: { id: number };
  tenant: { id: number; code: string } | null;
  warehouse: { id: number; code: string } | null;
  role: { applicative: string | null; realm: string[]; is_platform_admin: boolean };
  /** NEW (A5): plural roles in the current tenant scope. */
  roles?: { applicative: string[]; realm: string[]; is_platform_admin: boolean };
  /** NEW (A5): the default warehouse for this user in the current tenant. */
  default_warehouse?: { id: number; code: string; name: string } | null;
  /** NEW (A5): all warehouses this user is a member of in the current tenant. */
  warehouses?: PerWarehouseEntry[];
  memberships?: Membership[];
};

type MeState =
  | { kind: 'loading' }
  | { kind: 'ok'; me: MeData }
  | { kind: 'no-membership' }
  | { kind: 'multi-tenant' }
  | { kind: 'error'; message: string };

const MeContext = createContext<{ state: MeState; api: ReturnType<typeof buildApiClient> | null }>({ state: { kind: 'loading' }, api: null });

/** Decode the JWT payload (no verification — we trust react-oidc-context already validated it). */
function parseJwtRealmRoles(accessToken: string): string[] {
  try {
    const payload = accessToken.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const roles = json?.realm_access?.roles;
    return Array.isArray(roles) ? roles : [];
  } catch {
    return [];
  }
}

export function MeProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<MeState>({ kind: 'loading' });
  const [tenantCode, setTenantCode] = useState<string | null>(null);
  const [warehouseCode, setWarehouseCode] = useState<string | null>(null);

  const api = auth.user ? buildApiClient(() => auth.user ?? null, () => tenantCode, () => warehouseCode) : null;

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) return;
    const realmRoles = parseJwtRealmRoles(auth.user.access_token);
    const isPlatformAdmin = realmRoles.includes('platform-admin');

    // Platform-admin path: cross-tenant by realm-role, no tenant_membership required.
    // Synthesize a minimal MeData without calling /me (which requires X-Tenant-Id).
    if (isPlatformAdmin) {
      setState({
        kind: 'ok',
        me: {
          user: { id: 0 },
          tenant: null,
          warehouse: null,
          role: { applicative: 'platform-admin', realm: realmRoles, is_platform_admin: true },
        },
      });
      return;
    }

    const probeUrl = (import.meta.env.VITE_API_BASE || 'http://voice.test/api/v1') + '/me/memberships';
    axios.get(probeUrl, { headers: { Authorization: `Bearer ${auth.user.access_token}` } })
      .then((res) => {
        const memberships: Membership[] = res.data?.data ?? [];
        if (memberships.length === 0) { setState({ kind: 'no-membership' }); return; }
        // Multi-tenant means the user has memberships in MORE THAN ONE tenant.
        // A single user can hold multiple memberships within the same tenant
        // (e.g. tenant-admin + voice-supervisor) — that's still single-tenant.
        const distinctTenantIds = new Set(memberships.map((m) => m.tenant.id));
        if (distinctTenantIds.size > 1) {
          setState({ kind: 'multi-tenant' });
          return;
        }
        // All memberships are in the same tenant; pick any to derive the
        // tenant code. The /me endpoint resolves the highest-privilege
        // applicative role for that tenant.
        const m = memberships[0];
        setTenantCode(m.tenant.code);
        setWarehouseCode(m.warehouse?.code ?? null);
        const meUrl = (import.meta.env.VITE_API_BASE || 'http://voice.test/api/v1') + '/me';
        const headers: Record<string, string> = { Authorization: `Bearer ${auth.user!.access_token}`, 'X-Tenant-Id': m.tenant.code };
        if (m.warehouse) headers['X-Warehouse-Id'] = m.warehouse.code;
        return axios.get(meUrl, { headers });
      })
      .then((res) => {
        if (res === undefined) return;
        setState({ kind: 'ok', me: { ...res.data, memberships: undefined } });
      })
      .catch((err) => {
        if (err.response?.status === 403) { setState({ kind: 'no-membership' }); return; }
        setState({ kind: 'error', message: String(err.message ?? err) });
      });
  }, [auth.isAuthenticated, auth.user?.access_token]);

  return <MeContext.Provider value={{ state, api }}>{children}</MeContext.Provider>;
}

export function useMe() { return useContext(MeContext); }
